// src/scanAdmin/parse.js - Scan data (v133.118): the DSR SKU sales report, read in the browser. v136.16:
// also the weekly TEAL report and the Cubes / Falcon stock files (parseTeal, parseStock, below).
//
// The file's "data" sheet is one line per item on a bill: Branch, Bill Sale Date, Bill Serial No, Prod
// Code, Product Name, Class Store, Date/Time Created, Brand, Sub Brand 1, Main Group, Sub Group, Sub
// Segment, Discount Reason, Bill Remarks, Transaction Type, Selling Price, Avg. Cost, Wh soh (Qatar: "WH
// soh"), cubes store soh, Qty, Sales w/o VAT, VAT (UAE only), Discount Amount, Gross Amount, Total Avg.
// Cost, Profit, Profit%. Columns are found by their heading, never by position (the two countries differ).
//
// What leaves the browser is three summaries (src/worker/scan.js says what the Worker writes):
//   products  one per product: description, hierarchy, class, price, warehouse stock, stock in all
//             stores, average cost
//   daily     one per product per branch per day: units, sales w/o VAT, cost, discount
// "cubes store soh" is NOT a store's own stock: it is the same on every store's line for a product
// (checked on the 1-12 Oct files: 1,479 UAE and 719 Qatar products sold in two or more stores, none with
// a different figure per store), so it is the total for all stores and goes on the product. `stock`
// (a store's own stock) stays empty until a per-store stock report is uploaded.
// Deliberately NEVER read: Bill Serial No, Bill Remarks (loyalty and delivery-order numbers, which
// identify customers) and Discount Reason. Pure: no DOM, no XLSX, so the tests drive it directly.

import { normBarcode } from '../worker/scan.js';

const HEAD = {
  branch: ['branch'],
  date: ['bill sale date'],
  code: ['prod code'],
  name: ['product name'],
  cls: ['class store'],
  created: ['date/time created'],
  brand: ['brand'],
  subBrand: ['sub brand 1', 'sub brand'],
  mainGroup: ['main group'],
  subGroup: ['sub group'],
  subSegment: ['sub segment'],
  price: ['selling price'],
  avgCost: ['avg. cost', 'avg cost'],
  wh: ['wh soh'],
  soh: ['cubes store soh', 'store soh'],
  qty: ['qty'],
  sales: ['sales w/o vat'],
  discount: ['discount amount'],
  cost: ['total avg. cost', 'total avg cost'],
};
const REQUIRED = ['branch', 'date', 'code', 'name', 'qty', 'sales'];

const clean = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
const n = (v) => { if (v === null || v === undefined || v === '') return null; const x = Number(v); return Number.isFinite(x) ? x : null; };

// An Excel date: a serial number, a Date, or "2026-10-01..." / "01/10/2026". -> 'YYYY-MM-DD' or null.
export function isoDay(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) {
    const d = new Date(Math.round((v - 25569) * 86400000));
    return isNaN(d) ? null : d.toISOString().slice(0, 10);
  }
  if (v instanceof Date) {
    if (isNaN(v)) return null;
    // a Date built from a date-only cell is local midnight: read the local calendar day
    const y = v.getFullYear(), m = v.getMonth() + 1, d = v.getDate();
    return y + '-' + (m < 10 ? '0' : '') + m + '-' + (d < 10 ? '0' : '') + d;
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return m[1] + '-' + m[2] + '-' + m[3];
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);
  if (m) return m[3] + '-' + (m[2].length < 2 ? '0' : '') + m[2] + '-' + (m[1].length < 2 ? '0' : '') + m[1];
  return null;
}

export function findHeader(aoa) {
  for (let r = 0; r < Math.min(aoa.length, 15); r++) {
    const row = (aoa[r] || []).map((c) => clean(c).toLowerCase());
    if (row.indexOf('prod code') !== -1 && row.indexOf('branch') !== -1) {
      const col = {};
      Object.keys(HEAD).forEach((k) => {
        for (const name of HEAD[k]) { const i = row.indexOf(name); if (i !== -1) { col[k] = i; break; } }
      });
      return { row: r, col };
    }
  }
  return null;
}

export function countryOf(filename, branches) {
  const f = String(filename || '').toUpperCase();
  if (/QATAR/.test(f)) return 'QATAR';
  if (/UAE/.test(f)) return 'UAE';
  const doha = (branches || []).filter((b) => /DOHA|QATAR/i.test(b)).length;
  if (branches && branches.length && doha === branches.length) return 'QATAR';
  if (branches && branches.length && doha === 0) return 'UAE';
  return null;
}

// aoa: the sheet as an array of rows (XLSX.utils.sheet_to_json(ws, { header: 1, raw: true })).
export function parseDsr(aoa, filename) {
  if (!Array.isArray(aoa)) throw new Error('No rows found');
  const h = findHeader(aoa);
  if (!h) throw new Error('Could not find the heading row (expected "Branch" and "Prod Code" columns on the data sheet)');
  const missing = REQUIRED.filter((k) => h.col[k] === undefined);
  if (missing.length) throw new Error('Missing column(s): ' + missing.join(', '));
  const C = h.col;
  const get = (row, k) => (C[k] === undefined ? null : row[C[k]]);

  const products = new Map();   // code -> product (from its latest line)
  const daily = new Map();      // code|branch|day -> totals
  const branches = new Set();
  let lines = 0, start = null, end = null;

  for (let r = h.row + 1; r < aoa.length; r++) {
    const row = aoa[r];
    if (!row) continue;
    const branch = clean(get(row, 'branch'));
    const code = clean(get(row, 'code'));
    if (!branch || !code || /^grand total$/i.test(branch)) continue;
    const day = isoDay(get(row, 'date'));
    if (!day) continue;
    lines++;
    branches.add(branch);
    if (!start || day < start) start = day;
    if (!end || day > end) end = day;

    const prev = products.get(code);
    if (!prev || day >= prev._day) {
      products.set(code, {
        _day: day,
        prod_code: code,
        description: clean(get(row, 'name')),
        class_store: clean(get(row, 'cls')) || null,
        brand: clean(get(row, 'brand')) || null,
        sub_brand: clean(get(row, 'subBrand')) || null,
        main_group: clean(get(row, 'mainGroup')) || null,
        sub_group: clean(get(row, 'subGroup')) || null,
        sub_segment: clean(get(row, 'subSegment')) || null,
        selling_price: n(get(row, 'price')),
        avg_cost: n(get(row, 'avgCost')),
        wh_soh: n(get(row, 'wh')),
        stores_soh: n(get(row, 'soh')),
        created_on: isoDay(get(row, 'created')),
        last_sold: day,
      });
    }
    const k = code + '\u0001' + branch + '\u0001' + day;
    let d = daily.get(k);
    if (!d) { d = { prod_code: code, branch, sale_date: day, qty: 0, sales: 0, cost: 0, discount: 0 }; daily.set(k, d); }
    d.qty += n(get(row, 'qty')) || 0;
    d.sales += n(get(row, 'sales')) || 0;
    d.cost += n(get(row, 'cost')) || 0;
    d.discount += n(get(row, 'discount')) || 0;
  }
  if (!lines) throw new Error('No sales lines found under the heading row');
  const r2 = (x) => Math.round(x * 100) / 100;
  const out = {
    country: countryOf(filename, Array.from(branches)),
    periodStart: start,
    periodEnd: end,
    lines,
    branches: Array.from(branches).sort(),
    products: Array.from(products.values()).map((p) => { const o = Object.assign({}, p); delete o._day; return o; }),
    stock: [],   // a store's own stock is not in this file (see the note at the top)
    daily: Array.from(daily.values()).map((d) => Object.assign({}, d, { qty: r2(d.qty), sales: r2(d.sales), cost: r2(d.cost), discount: r2(d.discount) })),
  };
  return out;
}

export function chunk(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

// ── the catalogue files (v133.118, Matt, 14 Oct 2026: "yes build it") ─────────────────────────────
// Cubes "Product details main barcode": one row per product (sold or not) - Main Group, Sub Group, Sub
// Segment, Brand Name, Sub Brand 1, Product Code, Product Name, Barcode, Status, Class Store, Standard
// Price, Standard Cost (plus web code, VAT and the like, not read).
// Cubes "Price Matrix - linked barcode": one row per BARCODE linked to a product, exported for one branch
// - Branch Name, Product Code, Barcode, Price 1 (plus printers, POG and the like, not read).
// Neither carries anything about customers or staff. Barcodes are tidied by the Worker (normBarcode).

// One row per barcode, first product wins (the files have a handful of repeats, some only after tidying:
// "193052040299" and "193052040299-D"), so no upload ever writes the same barcode twice in one go.
export function uniqueBarcodes(list) {
  const seen = new Set();
  const out = [];
  let dropped = 0;
  for (const b of list) {
    const k = normBarcode(b.barcode);
    if (!k) { dropped++; continue; }
    if (seen.has(k)) { dropped++; continue; }
    seen.add(k);
    out.push(Object.assign({}, b, { barcode: k }));
  }
  return { rows: out, dropped };
}

const CAT_HEAD = {
  code: ['product code'],
  name: ['product name'],
  barcode: ['barcode'],
  status: ['status'],
  cls: ['class store'],
  brand: ['brand name', 'brand'],
  subBrand: ['sub brand 1', 'sub brand'],
  mainGroup: ['main group'],
  subGroup: ['sub group'],
  subSegment: ['sub segment'],
  stdPrice: ['standard price'],
  stdCost: ['standard cost'],
  branch: ['branch name'],
  price1: ['price 1'],
  created: ['launch date'],   // v136.20: the new master data file
};

function headerAt(aoa, need, map) {
  for (let r = 0; r < Math.min(aoa.length, 10); r++) {
    const row = (aoa[r] || []).map((c) => clean(c).toLowerCase());
    if (need.every((n) => row.indexOf(n) !== -1)) {
      const col = {};
      Object.keys(map).forEach((k) => { for (const name of map[k]) { const i = row.indexOf(name); if (i !== -1) { col[k] = i; break; } } });
      return { row: r, col, names: row };
    }
  }
  return null;
}

// Which file is this? 'dsr' (the daily sales report), 'teal' (the TEAL report by branch: a weekly total,
// or daily once it has a Date column), 'details', 'matrix', 'stock' (Cubes store stock or Falcon
// warehouse stock, or both in one file), 'promo' (v136.20: the active promotions), or null.
// v136.20: two SHEETS of the new master data file are 'products' (product code and name, no barcode, no
// branch) and 'barcodes' (product code and barcode, no name): pickSheet() puts the two together as one
// 'details' upload. Neither is a file on its own.
export function detectFile(aoa) {
  if (!Array.isArray(aoa)) return null;
  const d = findHeader(aoa);
  if (d) return d.col.date !== undefined ? 'dsr' : 'teal';   // the DSR has "Bill Sale Date"; the TEAL report none, or "Date"
  const h = headerAt(aoa, ['product code', 'barcode'], CAT_HEAD);
  if (h) {
    if (h.names.indexOf('branch name') !== -1 && h.names.indexOf('price 1') !== -1) return 'matrix';
    return h.names.indexOf('product name') !== -1 ? 'details' : 'barcodes';
  }
  if (headerAt(aoa, ['branch name', 'on-hand qty total'], STOCK_HEAD)) return 'stock';
  const p = headerAt(aoa, ['branch', 'discounted selling price'], PROMO_HEAD);
  if (p && p.col.code !== undefined) return 'promo';
  const pr = headerAt(aoa, ['product code', 'product name'], CAT_HEAD);
  if (pr && ['branch', 'branch name', 'on-hand qty total'].every((x) => pr.names.indexOf(x) === -1)) return 'products';
  return null;
}

// v136.20: which sheet(s) of a workbook to read. names: the sheet names in order; rowsOf(name): that
// sheet as rows (only called for the sheets looked at). A sheet that is a whole file on its own wins (the
// DSR's "data" sheet first); else a Products sheet, with a Barcodes sheet if there is one, is one
// 'details' upload. Returns { kind, aoa, barcodes? } or null.
const FILE_KINDS = ['dsr', 'teal', 'details', 'matrix', 'stock', 'promo'];
export function pickSheet(names, rowsOf) {
  const order = names.indexOf('data') !== -1 ? ['data'].concat(names.filter((n) => n !== 'data')) : names.slice();
  let products = null, barcodes = null;
  for (const n of order) {
    const aoa = rowsOf(n);
    const kind = detectFile((aoa || []).slice(0, 15));
    if (FILE_KINDS.indexOf(kind) !== -1) return { kind, aoa };
    if (kind === 'products' && !products) products = aoa;
    if (kind === 'barcodes' && !barcodes) barcodes = aoa;
  }
  if (products) return { kind: 'details', aoa: products, barcodes };
  if (barcodes) throw new Error('This workbook has a Barcodes sheet but no Products sheet (with "Product Code" and "Product Name"), so it was not loaded.');
  return null;
}

// ── the TEAL report and the stock files (v136.16, Matt, 19 Oct 2026: "using the data ive given you ...
// build something i could at least trial in live") ───────────────────────────────────────────────
// "TEAL Report - By Branch - Product" (UAE and Qatar, 12 - 18 Oct 2026): a Grand Total line, then
// Branch, Prod Code, Product Name, Brand, Product.Sub Brand 1, Main Group, Sub Group, Sales w/o VAT,
// Qty, Total Avg. Cost, Profit (Avg. Cost), Profit%. One TOTAL per product per store for the week: no
// date column, no bills, nothing about customers. The week comes from the file name ("12 - 18 Oct
// 2026"), or is asked for. With a Date column (what Head Office is asked for) it is daily, like the DSR.
// "Cubes Current SOH" (stores) and "Falcon Current SOH" (warehouse): Branch Name, the product code
// (UAE "Product Name.Product Code", Qatar "Code", Falcon "Product Code"), ..., On-Hand Qty Total.

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const ymd = (y, m, d) => y + '-' + (m < 10 ? '0' : '') + m + '-' + (d < 10 ? '0' : '') + d;
const okDay = (y, m, d) => m >= 1 && m <= 12 && d >= 1 && d <= 31 && y > 2000;

// "..._12_-_18_Oct_2026.xlsx", "28 Sep - 4 Oct 2026" -> { start, end }, or null.
export function periodFromName(filename) {
  const f = String(filename || '').replace(/[_]+/g, ' ').toUpperCase();
  const mon = (s) => MONTHS.indexOf(String(s).slice(0, 3)) + 1;
  let m = f.match(/(\d{1,2})\s+([A-Z]{3,9})\s*-\s*(\d{1,2})\s+([A-Z]{3,9})\s+(\d{4})/);
  if (m && mon(m[2]) && mon(m[4])) {
    const y2 = Number(m[5]), m1 = mon(m[2]), m2 = mon(m[4]);
    const y1 = m1 > m2 ? y2 - 1 : y2;
    if (okDay(y1, m1, +m[1]) && okDay(y2, m2, +m[3])) return { start: ymd(y1, m1, +m[1]), end: ymd(y2, m2, +m[3]) };
  }
  m = f.match(/(\d{1,2})\s*-\s*(\d{1,2})\s+([A-Z]{3,9})\s+(\d{4})/);
  if (m && mon(m[3]) && +m[1] <= +m[2] && okDay(+m[4], mon(m[3]), +m[2])) return { start: ymd(+m[4], mon(m[3]), +m[1]), end: ymd(+m[4], mon(m[3]), +m[2]) };
  return null;
}

// "..._SOH_20261019.xlsx" -> '2026-10-19', or null.
export function asOfFromName(filename) {
  const f = String(filename || '');
  let m = f.match(/(20\d{2})-?(\d{2})-?(\d{2})(?!\d)/);
  if (m && okDay(+m[1], +m[2], +m[3])) return ymd(+m[1], +m[2], +m[3]);
  return null;
}

const TEAL_HEAD = {
  branch: ['branch', 'branch name'],
  code: ['prod code', 'product code'],
  date: ['date', 'sale date', 'bill sale date'],
  qty: ['qty'],
  sales: ['sales w/o vat'],
  cost: ['total avg. cost', 'total avg cost'],
};

// period: { start, end } for a file with no Date column (from the name, or asked for). Throws
// NEED_PERIOD when there is neither.
export function parseTeal(aoa, filename, period) {
  const h = headerAt(aoa, ['qty', 'sales w/o vat'], TEAL_HEAD);
  if (!h || h.col.branch === undefined || h.col.code === undefined) throw new Error('Could not find the heading row (expected "Branch", "Prod Code", "Qty" and "Sales w/o VAT")');
  const C = h.col;
  const g = (row, k) => (C[k] === undefined ? null : row[C[k]]);
  const dated = C.date !== undefined;
  const p = dated ? null : (period || periodFromName(filename));
  if (!dated && !p) { const e = new Error('NEED_PERIOD'); e.needPeriod = true; throw e; }
  const daily = new Map();
  const branches = new Set();
  const codes = new Set();
  let lines = 0, start = p ? p.start : null, end = p ? p.end : null;
  for (let r = h.row + 1; r < aoa.length; r++) {
    const row = aoa[r];
    if (!row) continue;
    const branch = clean(g(row, 'branch'));
    const code = clean(g(row, 'code'));
    if (!branch || !code || /total/i.test(branch)) continue;
    const day = dated ? isoDay(g(row, 'date')) : p.end;
    if (!day) continue;
    lines++;
    branches.add(branch); codes.add(code);
    if (dated) { if (!start || day < start) start = day; if (!end || day > end) end = day; }
    const k = code + '\u0001' + branch + '\u0001' + day;
    let d = daily.get(k);
    if (!d) { d = { prod_code: code, branch, sale_date: day, qty: 0, sales: 0, cost: 0, discount: 0 }; daily.set(k, d); }
    d.qty += n(g(row, 'qty')) || 0;
    d.sales += n(g(row, 'sales')) || 0;
    d.cost += n(g(row, 'cost')) || 0;
  }
  if (!lines) throw new Error('No sales lines found under the heading row');
  const r2 = (x) => Math.round(x * 100) / 100;
  return {
    kind: 'teal',
    country: countryOf(filename, Array.from(branches)),
    periodStart: start, periodEnd: end,
    totalsOnly: !dated,
    lines,
    skus: codes.size,
    branches: Array.from(branches).sort(),
    products: [],   // the product details come from the catalogue: this file never overwrites them
    stock: [],
    daily: Array.from(daily.values()).map((d) => Object.assign({}, d, { qty: r2(d.qty), sales: r2(d.sales), cost: r2(d.cost) })),
  };
}

const STOCK_HEAD = {
  branch: ['branch name'],
  code: ['product code', 'product name.product code', 'code'],
  qty: ['on-hand qty total'],
  // v136.20: the new stock files ("2 - Stock UAE", "3 - Stock Qatar") also carry each store's own price,
  // what is on order and, under "Expected Date", the LPO date (the day the order was raised: Cubes leaves
  // the real expected date blank, so the column is filled with the LPO date). Never shown as "due".
  price: ['selling price aed', 'selling price qar', 'selling price'],
  onOrder: ['on order qty', 'on order'],
  orderDate: ['lpo date', 'code.lpo date', 'expected date'],
};

// v136.20 (Matt, 20 Oct 2026: "head office i believe is warehous"): the new files list the warehouse as the
// branch "HEAD OFFICE" (UAE) or "Head Office" (Qatar).
export function isWarehouseBranch(branch) {
  const b = String(branch == null ? '' : branch).replace(/\s+/g, ' ').trim();
  return /warehouse/i.test(b) || /^head office$/i.test(b);
}

// The price most stores charge for each product (prices of 0 or none are left out; a tie goes to the higher).
function usualPrices(byCode) {
  const out = new Map();
  byCode.forEach((counts, code) => {
    let best = null, top = 0;
    counts.forEach((c, p) => { if (c > top || (c === top && p > best)) { top = c; best = p; } });
    if (best != null) out.set(code, best);
  });
  return out;
}

// Branch names with "warehouse" in them, and "Head Office", are the warehouse (Falcon); the rest are
// stores (Cubes). The Cubes file lists every product in every store (243,000 lines for the UAE, about
// 27,000 of them not zero), so a store line is kept only when it says something: stock that is not zero,
// something on order, or (v136.20) a price that is not the one most stores charge. The usual price goes on
// the product (`prices`), so a store with no line charges that. The warehouse keeps every line, zeros
// included. Negative stock is kept as it is and counted. The price, on-order and LPO-date fields are on the
// lines only when the file has those columns, so an older file never blanks them.
export function parseStock(aoa, filename) {
  const h = headerAt(aoa, ['branch name', 'on-hand qty total'], STOCK_HEAD);
  if (!h || h.col.code === undefined) throw new Error('Could not find the heading row (expected "Branch Name", the product code and "On-Hand Qty Total")');
  const C = h.col;
  const g = (row, k) => (C[k] === undefined ? null : row[C[k]]);
  const hasPrice = C.price !== undefined, hasOrder = C.onOrder !== undefined, hasDate = C.orderDate !== undefined;
  const stock = new Map();
  const wh = new Map();
  const stores = new Set();
  const warehouses = new Set();
  const priceCounts = new Map();   // code -> Map(price -> stores)
  let negative = 0, lines = 0;
  for (let r = h.row + 1; r < aoa.length; r++) {
    const row = aoa[r];
    if (!row) continue;
    const branch = clean(g(row, 'branch'));
    const code = clean(g(row, 'code'));
    if (!branch || !code || /total/i.test(branch)) continue;
    const q = n(g(row, 'qty')) || 0;
    lines++;
    if (q < 0) negative++;
    if (isWarehouseBranch(branch)) {
      warehouses.add(branch);
      wh.set(code, (wh.get(code) || 0) + q);
      continue;
    }
    stores.add(branch);
    const price = hasPrice ? n(g(row, 'price')) : null;
    const p = price != null && price > 0 ? price : null;
    if (p != null) {
      let m = priceCounts.get(code);
      if (!m) { m = new Map(); priceCounts.set(code, m); }
      m.set(p, (m.get(p) || 0) + 1);
    }
    const k = code + '\u0001' + branch;
    const s = stock.get(k);
    const oo = hasOrder ? n(g(row, 'onOrder')) : null;
    if (s) {
      s.store_soh += q;
      if (hasOrder && oo) s.on_order = (s.on_order || 0) + oo;
      continue;
    }
    const line = { prod_code: code, branch, store_soh: q };
    if (hasPrice) line.store_price = p;
    if (hasOrder) line.on_order = oo ? oo : null;
    if (hasDate) line.on_order_date = line.on_order ? isoDay(g(row, 'orderDate')) : null;
    stock.set(k, line);
  }
  if (!lines) throw new Error('No stock lines found under the heading row');
  const usual = usualPrices(priceCounts);
  const kept = Array.from(stock.values()).filter((s) => s.store_soh !== 0 || s.on_order ||
    (s.store_price != null && usual.has(s.prod_code) && s.store_price !== usual.get(s.prod_code)));
  const all = Array.from(stores).concat(Array.from(warehouses));
  return {
    kind: 'stock',
    country: countryOf(filename, all),
    asOf: asOfFromName(filename),
    lines, negative,
    stores: Array.from(stores).sort(),
    warehouses: Array.from(warehouses).sort(),
    stock: kept,
    onOrderLines: kept.filter((s) => s.on_order).length,
    hasPrice, hasOrder,
    // the price most stores charge, on the product (only when the file has prices)
    prices: Array.from(usual.entries()).map(([prod_code, shelf_price]) => ({ prod_code, shelf_price, shelf_price_branch: 'most stores (stock file)' })),
    warehouse: Array.from(wh.entries()).map(([prod_code, wh_soh]) => ({ prod_code, wh_soh })),
  };
}

// ── the active promotions (v136.20) ─────────────────────────────────────────────────────────────────
// "6 - UAE promotion active data" (20 Oct 2026; a Qatar file may follow, so everything is per
// country): one row per branch per product on promotion - Branch, Code (the promotion's name), Code.Discount
// Reason, Code.Price Mode, Code.Start Date, Code.End Date, Product.Product Code, Product, Standard Selling
// Price, Disc Amt, Disc % (a fraction: 0.58 is 58%), Discounted Selling Price. Each file is the WHOLE active
// list for its country, so it replaces what was there. Where one product is on two promotions in the same
// branch, the lower promotion price is kept (the other is counted in `skipped`).
const PROMO_HEAD = {
  branch: ['branch', 'branch name'],
  name: ['code', 'promotion', 'promotion name'],
  reason: ['code.discount reason', 'discount reason'],
  mode: ['code.price mode', 'price mode'],
  start: ['code.start date', 'start date'],
  end: ['code.end date', 'end date'],
  code: ['product.product code', 'product code'],
  std: ['standard selling price'],
  amt: ['disc amt', 'discount amount'],
  pct: ['disc %', 'discount %'],
  price: ['discounted selling price'],
};

export function parsePromo(aoa, filename) {
  const h = headerAt(aoa, ['branch', 'discounted selling price'], PROMO_HEAD);
  if (!h || h.col.code === undefined) throw new Error('Could not find the heading row (expected "Branch", "Product.Product Code" and "Discounted Selling Price")');
  const C = h.col;
  const g = (row, k) => (C[k] === undefined ? null : row[C[k]]);
  const rows = new Map();
  const branches = new Set();
  const names = new Set();
  let lines = 0, skipped = 0, maxPct = 0;
  for (let r = h.row + 1; r < aoa.length; r++) {
    const row = aoa[r];
    if (!row) continue;
    const branch = clean(g(row, 'branch'));
    const code = clean(g(row, 'code'));
    if (!branch || !code || /total/i.test(branch)) continue;
    lines++;
    branches.add(branch);
    const name = clean(g(row, 'name')) || null;
    if (name) names.add(name);
    const pct = n(g(row, 'pct'));
    if (pct != null && Math.abs(pct) > maxPct) maxPct = Math.abs(pct);
    const o = {
      prod_code: code, branch, promo_name: name,
      reason: clean(g(row, 'reason')) || null,
      price_mode: clean(g(row, 'mode')) || null,
      start_date: isoDay(g(row, 'start')), end_date: isoDay(g(row, 'end')),
      std_price: n(g(row, 'std')), disc_amt: n(g(row, 'amt')), disc_pct: pct,
      promo_price: n(g(row, 'price')),
    };
    const k = code + '\u0001' + branch;
    const prev = rows.get(k);
    if (prev) {
      skipped++;
      const a = prev.promo_price == null ? Infinity : prev.promo_price, b = o.promo_price == null ? Infinity : o.promo_price;
      if (b < a || (b === a && String(o.start_date || '') > String(prev.start_date || ''))) rows.set(k, o);
      continue;
    }
    rows.set(k, o);
  }
  if (!lines) throw new Error('No promotion lines found under the heading row');
  // "Disc %" is a fraction in the file (Excel's percent format): kept as a percentage
  const asPct = maxPct > 0 && maxPct <= 1;
  const out = Array.from(rows.values()).map((o) => (asPct && o.disc_pct != null ? Object.assign({}, o, { disc_pct: Math.round(o.disc_pct * 10000) / 100 }) : o));
  return {
    kind: 'promo',
    country: countryOf(filename, Array.from(branches)),
    asOf: asOfFromName(filename),
    lines, skipped,
    promotions: names.size,
    branches: Array.from(branches).sort(),
    rows: out,
  };
}

// v136.20: the catalogue fields, and the heading each is read from. A field whose column the file does not
// have is LEFT OUT of every row (not sent as empty), so the Worker never blanks what an earlier file loaded:
// the new master data file has no prices, no costs and no store class, and must not clear them.
const CAT_FIELDS = [
  ['class_store', 'cls', 'text'], ['brand', 'brand', 'text'], ['sub_brand', 'subBrand', 'text'],
  ['main_group', 'mainGroup', 'text'], ['sub_group', 'subGroup', 'text'], ['sub_segment', 'subSegment', 'text'],
  ['status', 'status', 'text'], ['standard_price', 'stdPrice', 'num'], ['standard_cost', 'stdCost', 'num'],
  ['created_on', 'created', 'day'],
];

// The old one-sheet "Product details main barcode" file (code, name and barcode on one sheet), or (v136.20)
// the new master data file: a Products sheet (aoa) and a Barcodes sheet (barcodesAoa: Product Code, Barcode).
export function parseCatalogue(aoa, filename, barcodesAoa) {
  const h = headerAt(aoa, ['product code', 'product name'], CAT_HEAD);
  if (!h) throw new Error('Could not find the heading row (expected "Product Code" and "Product Name")');
  const C = h.col;
  const g = (row, k) => (C[k] === undefined ? null : row[C[k]]);
  const fields = CAT_FIELDS.filter((f) => C[f[1]] !== undefined);
  const products = new Map();
  const barcodes = [];
  for (let r = h.row + 1; r < aoa.length; r++) {
    const row = aoa[r];
    if (!row) continue;
    const code = clean(g(row, 'code'));
    if (!code) continue;
    if (!products.has(code)) {
      const p = { prod_code: code, description: clean(g(row, 'name')) };
      fields.forEach(([key, col, type]) => {
        const v = g(row, col);
        p[key] = type === 'num' ? n(v) : (type === 'day' ? isoDay(v) : (clean(v) || null));
      });
      products.set(code, p);
    }
    const b = clean(g(row, 'barcode'));
    if (b) barcodes.push({ prod_code: code, barcode: b, source: 'main' });
  }
  if (!products.size) throw new Error('No products found under the heading row');
  if (barcodesAoa) {
    const hb = headerAt(barcodesAoa, ['product code', 'barcode'], CAT_HEAD);
    if (!hb) throw new Error('Could not find the heading row of the Barcodes sheet (expected "Product Code" and "Barcode")');
    for (let r = hb.row + 1; r < barcodesAoa.length; r++) {
      const row = barcodesAoa[r];
      if (!row) continue;
      const code = clean(row[hb.col.code]);
      const b = clean(row[hb.col.barcode]);
      if (code && b) barcodes.push({ prod_code: code, barcode: b, source: 'main' });
    }
  }
  const u = uniqueBarcodes(barcodes);
  return { kind: 'details', country: countryOf(filename, []), products: Array.from(products.values()), barcodes: u.rows, skipped: u.dropped, fields: ['description'].concat(fields.map((f) => f[0])) };
}

export function parseMatrix(aoa, filename) {
  const h = headerAt(aoa, ['product code', 'barcode', 'branch name'], CAT_HEAD);
  if (!h) throw new Error('Could not find the heading row (expected "Branch Name", "Product Code" and "Barcode")');
  const C = h.col;
  const g = (row, k) => (C[k] === undefined ? null : row[C[k]]);
  const prices = new Map();
  const barcodes = [];
  const branches = new Set();
  for (let r = h.row + 1; r < aoa.length; r++) {
    const row = aoa[r];
    if (!row) continue;
    const code = clean(g(row, 'code'));
    if (!code) continue;
    const branch = clean(g(row, 'branch'));
    if (branch) branches.add(branch);
    if (!prices.has(code) && n(g(row, 'price1')) !== null) prices.set(code, { prod_code: code, shelf_price: n(g(row, 'price1')), shelf_price_branch: branch || null });
    const b = clean(g(row, 'barcode'));
    if (b) barcodes.push({ prod_code: code, barcode: b, source: 'linked' });
  }
  if (!barcodes.length && !prices.size) throw new Error('No barcodes found under the heading row');
  const u = uniqueBarcodes(barcodes);
  return { kind: 'matrix', country: countryOf(filename, Array.from(branches)), branches: Array.from(branches).sort(), prices: Array.from(prices.values()), barcodes: u.rows, skipped: u.dropped };
}
